import { BattleResultType } from '../types/BattleState';
import { SceneKey } from './sceneKeys';
import { GameMode } from '../types/GameMode';

/** リザルト画面レイアウト定数 */
export interface ResultLayoutConfig {
  /** 勝敗テキストのY座標 */
  resultTextY: number;
  /** HP表示エリアのY座標 */
  hpDisplayY: number;
  /** 成長差分表示エリアの開始Y座標（CHALLENGEモード勝利時） */
  growthDisplayY: number;
  /** ボタン開始Y座標 */
  buttonStartY: number;
  /** ボタン間隔 */
  buttonSpacing: number;
  /** シェアボタンのY座標 */
  shareButtonY: number;
}

/** リザルト画面レイアウト */
export const RESULT_LAYOUT: ResultLayoutConfig = {
  resultTextY: 80,
  hpDisplayY: 160,
  growthDisplayY: 210,
  buttonStartY: 390,
  buttonSpacing: 42,
  shareButtonY: 510,
};

/** リザルト画面の色設定 */
export interface ResultColorConfig {
  /** 勝利テキスト色 */
  winColor: number;
  /** 敗北テキスト色 */
  loseColor: number;
  /** ドローテキスト色 */
  drawColor: number;
  /** 通常テキスト色 */
  textColor: string;
  /** ボタン背景色 */
  buttonBg: number;
  /** ボタンテキスト色 */
  buttonText: string;
}

/** リザルト画面色設定 */
export const RESULT_COLORS: ResultColorConfig = {
  winColor: 0x44cc44,
  loseColor: 0xcc4444,
  drawColor: 0xcccc44,
  textColor: '#ffffff',
  buttonBg: 0x445588,
  buttonText: '#ffffff',
};

/** 勝敗テキスト */
export const RESULT_TEXT: Record<BattleResultType, string> = {
  [BattleResultType.PLAYER1_WIN]: '勝利！',
  [BattleResultType.PLAYER2_WIN]: '敗北...',
  [BattleResultType.DRAW]: '引き分け',
};

/** ボタン設定 */
export interface ResultButtonConfig {
  /** ボタンラベル */
  label: string;
  /** 遷移先シーン */
  targetScene: SceneKey;
}

/** リザルト画面のデフォルトボタン定義（表示順） */
export const RESULT_BUTTON_CONFIG: ResultButtonConfig[] = [
  {
    label: '次へ',
    targetScene: SceneKey.CHARACTER_SELECT,
  },
  {
    label: 'リトライ',
    targetScene: SceneKey.CHARACTER_SELECT,
  },
  {
    label: 'タイトルへ',
    targetScene: SceneKey.TITLE,
  },
];

/** FREE_CPUモード用のボタン定義 */
const FREE_CPU_BUTTON_CONFIG: ResultButtonConfig[] = [
  {
    label: 'もう一度',
    targetScene: SceneKey.CHARACTER_SELECT,
  },
  {
    label: 'タイトルへ',
    targetScene: SceneKey.TITLE,
  },
];

/** FREE_LOCALモード用のボタン定義（リマッチ未実装のため再戦時は部屋を立て直す） */
const FREE_LOCAL_BUTTON_CONFIG: ResultButtonConfig[] = [
  {
    label: 'タイトルへ',
    targetScene: SceneKey.TITLE,
  },
];

/** SPECTATORモード用のボタン定義 */
const SPECTATOR_BUTTON_CONFIG: ResultButtonConfig[] = [
  {
    label: 'もう一度',
    targetScene: SceneKey.VERSUS_SELECT,
  },
  {
    label: 'タイトルへ',
    targetScene: SceneKey.TITLE,
  },
];

/** CHALLENGE用オプション */
export interface ChallengeResultOptions {
  /** 現在のステージ番号 */
  stageNumber?: number;
  /** バトル結果タイプ */
  resultType?: BattleResultType;
  /** 再プレイかどうか */
  isReplay?: boolean;
}

/** FREE_CPU隠しキャラ挑戦勝利用ボタン（もう一度なし） */
const FREE_CPU_HIDDEN_CHALLENGE_WIN_CONFIG: ResultButtonConfig[] = [
  {
    label: 'タイトルへ',
    targetScene: SceneKey.TITLE,
  },
];

/**
 * モードに応じたボタン定義を返す
 */
export function getResultButtons(
  mode?: GameMode,
  options?: ChallengeResultOptions,
  isHiddenChallengeWin?: boolean,
): ResultButtonConfig[] {
  if (isHiddenChallengeWin) {
    return FREE_CPU_HIDDEN_CHALLENGE_WIN_CONFIG;
  }
  if (mode === GameMode.CHALLENGE && options) {
    return getChallengeResultButtons(options.stageNumber, options.resultType, options.isReplay);
  }
  if (mode === GameMode.FREE_CPU) {
    return FREE_CPU_BUTTON_CONFIG;
  }
  if (mode === GameMode.FREE_LOCAL) {
    return FREE_LOCAL_BUTTON_CONFIG;
  }
  if (mode === GameMode.SPECTATOR) {
    return SPECTATOR_BUTTON_CONFIG;
  }
  return RESULT_BUTTON_CONFIG;
}

/**
 * CHALLENGE用ボタン定義を返す
 */
function getChallengeResultButtons(
  stageNumber?: number,
  resultType?: BattleResultType,
  isReplay?: boolean,
): ResultButtonConfig[] {
  const isWin = resultType === BattleResultType.PLAYER1_WIN;

  // 再プレイ時
  if (isReplay) {
    if (isWin) {
      return [
        { label: 'ステージ選択へ', targetScene: SceneKey.STAGE_SELECT },
        { label: 'タイトルへ', targetScene: SceneKey.TITLE },
      ];
    }
    // 敗北・ドロー
    return [
      { label: 'リトライ', targetScene: SceneKey.BATTLE },
      { label: 'ステージ選択へ', targetScene: SceneKey.STAGE_SELECT },
      { label: 'タイトルへ', targetScene: SceneKey.TITLE },
    ];
  }

  // 通常プレイ
  const isBossStage = stageNumber === 9;

  if (isBossStage) {
    // ボス戦: 勝敗ともにステージ選択へ（3体選択からやり直し）
    return [
      { label: 'ステージ選択へ', targetScene: SceneKey.STAGE_SELECT },
      { label: 'タイトルへ', targetScene: SceneKey.TITLE },
    ];
  }

  if (isWin) {
    return [
      { label: 'ステージ選択へ', targetScene: SceneKey.STAGE_SELECT },
      { label: 'タイトルへ', targetScene: SceneKey.TITLE },
    ];
  }

  // 敗北・ドロー
  return [
    { label: 'リトライ', targetScene: SceneKey.BATTLE },
    { label: 'タイトルへ', targetScene: SceneKey.TITLE },
  ];
}
